/**
 * Dataspace Context Store (Orgs v1)
 * 
 * Manages the user's active dataspace context (Personal vs Org).
 * Handles switching between dataspaces and loading user's organizations.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';

const log = createFileLogger('DATASPACE_CONTEXT_STORE', {enabled: false});

// Types
export interface DataspaceContext {
  type: 'personal' | 'org';
  orgId: string | null;
  orgName: string | null;
}

export interface PendingNavigation {
  type: 'space' | 'home';
  spaceId?: string;
}

export interface Org {
  _id: string;
  id: string;
  entityType: 'Org';
  name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  skeleton: {
    '@type'?: string;
    displayName: string;
    slug: string;
    description?: string;
    logo_url?: string;
    owner_user_id: string;
    member_user_ids: string[];
    admin_user_ids: string[];
    guest_user_ids: string[];
    orggroup_ids: string[];
    root_group_ids: string[];
    billing_plan: string;
    billing_status?: string; // 'payment_required' | 'active' | 'past_due' | etc
    billing_schema_version?: string; // Billing plan schema version (v3, v4, ...)
    seat_count?: number;
    shared_credit_balance: number;
    monthly_credit_refresh: number;
  };
  acl: any[];
}

export interface PaymentGateState {
  isOpen: boolean;
  orgId: string | null;
  orgName: string | null;
  seatCount: number;
  planTier?: string;  // ✅ FIX #6: Track selected plan tier
}

/**
 * A personal workspace is one the user owns alone with no other members.
 * Slug-based checks are unreliable: auto-created orgs use the display name as slug.
 */
export function isPersonalOrg(org: Org, currentUserId?: string): boolean {
  const skel = org.skeleton;
  const isOwner = currentUserId ? skel.owner_user_id === currentUserId : true;
  return isOwner && (skel.member_user_ids?.length ?? 0) === 0;
}

/**
 * Find the current user's personal workspace org from a list of orgs.
 */
export function findPersonalOrg(orgs: Org[], currentUserId: string): Org | undefined {
  return orgs.find(org => isPersonalOrg(org, currentUserId));
}

interface DataspaceContextState {
  // State
  currentContext: DataspaceContext;
  userOrgs: Org[];
  isLoading: boolean;
  error: string | null;
  pendingNavigation: PendingNavigation | null;
  lastVisitedSpaces: Record<string, string>; // Maps dataspace key (personal or orgId) to spaceId
  paymentGate: PaymentGateState;
  
  // Actions
  setContext: (context: DataspaceContext) => Promise<void>;
  loadUserOrgs: () => Promise<void>;
  switchToPersonal: () => Promise<void>;
  switchToOrg: (orgId: string) => Promise<void>;
  refreshContext: () => Promise<void>;
  clearError: () => void;
  setPendingNavigation: (nav: PendingNavigation | null) => void;
  setLastVisitedSpace: (spaceId: string) => void;
  getLastVisitedSpace: (contextKey?: string) => string | null;
  showPaymentGate: (orgId: string) => void;
  hidePaymentGate: () => void;
      updatePaymentGatePlan: (planTier: string) => void;  // ✅ FIX #6: Update plan selection
}

/**
 * Dataspace Context Store
 * 
 * Manages switching between Personal and Org dataspaces.
 * Persists the current context to localStorage and backend.
 */
export const useDataspaceContextStore = create<DataspaceContextState>()(
  persist(
    (set, get) => ({
      // Initial state — orgId is null until synced from backend via GET /user/context.
      // type is always 'org'; every user has a personal org created at signup.
      currentContext: {
        type: 'org',
        orgId: null,
        orgName: null,
      },
      userOrgs: [],
      isLoading: false,
      error: null,
      pendingNavigation: null,
      lastVisitedSpaces: {},
      paymentGate: {
        isOpen: false,
        orgId: null,
        orgName: null,
        seatCount: 1
      },
      
      // Set dataspace context
      setContext: async (context: DataspaceContext) => {
        set({ isLoading: true, error: null });
        
        try {
          log.info('[setContext] Switching to', context);
          
          // Flush all pending saves BEFORE switching dataspace to prevent data loss.
          // Uses a simple retry loop: transient network errors are retried up to 2 times.
          // If all retries fail we log a warning and continue — blocking the switch
          // permanently on a save failure would leave the user stuck.
          const MAX_FLUSH_RETRIES = 2;
          const FLUSH_RETRY_DELAY_MS = 600;
          let flushAttempt = 0;
          let flushSucceeded = false;
          while (flushAttempt <= MAX_FLUSH_RETRIES && !flushSucceeded) {
            if (flushAttempt > 0) {
              log.warn(`[setContext] Retrying saves flush (attempt ${flushAttempt}/${MAX_FLUSH_RETRIES})...`);
              await new Promise(r => setTimeout(r, FLUSH_RETRY_DELAY_MS));
            }
            try {
              log.info('[setContext] 🔄 Flushing all pending saves before dataspace switch...');

              const { docsCRDTService } = await import('../apps/DocsApp/services/crdt/docsCRDTService');
              await docsCRDTService.flushAllPendingSaves();
              log.info('[setContext] ✅ DocsApp saves flushed');

              const { notesCRDTService } = await import('../apps/NotesMarkdownApp/services/notesCRDTService');
              await notesCRDTService.flushAllPendingSaves();
              log.info('[setContext] ✅ NotesApp saves flushed');

              log.info('[setContext] ✅ All saves flushed successfully');
              flushSucceeded = true;
            } catch (flushError) {
              flushAttempt++;
              log.error(`[setContext] ⚠️ Error flushing saves (attempt ${flushAttempt}):`, flushError);
            }
          }
          if (!flushSucceeded) {
            log.error('[setContext] ❌ All flush retries failed — proceeding with context switch. Any unsaved changes in open documents may be lost.');
          }
          
          // Persist context to backend
          const data = await fetchService(`${API_BASE_URL}/user/context`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              context_type: context.type,
              org_id: context.orgId
            })
          });
          
          const newContext = {
            type: data.type,
            orgId: data.org_id || null,
            orgName: data.org_name || null
          };

          // Write this store first, then mirror into systemStore atomically (finding #7).
          set({ currentContext: newContext });
          try {
            const { useSystemStore } = await import('./systemStore');
            useSystemStore.setState({ currentContext: newContext });
          } catch {
            // non-fatal — context divergence canary will flag it in dev
          }

          // ✅ ORGS V1: Switch to dataspace-specific entity cache
          try {
            const { default: useEntityStore } = await import('./entityStore');
            const entityStore = useEntityStore.getState();
            
            // Determine dataspace key
            const dataspaceKey = data.type === 'org' && data.org_id ? data.org_id : 'personal';

            // Persist current entities to their dataspace cache via setState (finding #13:
            // direct property mutation bypasses Zustand subscribers).
            if (entityStore.currentDataspaceKey && entityStore.entities.length > 0) {
              const prevKey = entityStore.currentDataspaceKey;
              useEntityStore.setState(s => ({
                dataspaceEntities: {
                  ...s.dataspaceEntities,
                  [prevKey]: [...s.entities],
                },
              }));
              log.info(`[setContext] 💾 Saved ${entityStore.entities.length} entities for ${prevKey}`);
            }

            // Switch to new dataspace
            useEntityStore.setState({ currentDataspaceKey: dataspaceKey });
            log.info(`[setContext] ✅ Updated currentDataspaceKey to: ${dataspaceKey}`);

            // Clear IndexedDB cache for this dataspace to force fresh fetch
            try {
              const { entityCacheService } = await import('../services/entityCacheService');
              const { getCurrentUserId } = await import('../utils/userIdUtils');
              const { getDataspaceCacheKey } = await import('../utils/entityCacheUtils');
              const userId = getCurrentUserId();

              if (userId) {
                const cacheKeyToDelete = getDataspaceCacheKey(userId, data.type, data.org_id);
                await entityCacheService.delete(cacheKeyToDelete);
                log.info(`[setContext] 🗑️ Cleared stale cache for dataspace: ${cacheKeyToDelete.substring(0, 40)}...`);
              }
            } catch (cacheError) {
              log.warn(`[setContext] Failed to clear cache:`, cacheError);
            }

            // Clear the in-memory cache for this dataspace via setState and read the
            // (now-absent) entry to decide whether to fast-path from cache or re-fetch.
            // (Finding #22: deleting then immediately reading the same key was always
            // undefined — the cached-entities branch was dead code. Keep the delete but
            // remove the impossible cache hit.)
            useEntityStore.setState(s => {
              const next = { ...s.dataspaceEntities };
              delete next[dataspaceKey];
              return { dataspaceEntities: next };
            });

            // Trigger fresh entity fetch for the new dataspace.
            useEntityStore.setState({
              entities: [],
              initialized: false,
              status: 'idle',
              lifecycle: 'uninitialized',
              initializationPromise: null,
            });
            log.info(`[setContext] 🔄 Triggering entity reload for dataspace: ${dataspaceKey}`);

            const { getCurrentUserId } = await import('../utils/userIdUtils');
            const userId = getCurrentUserId();

            const crdtRoomId = data.type === 'org' && data.org_id
              ? `org:${data.org_id}`
              : `user:${userId}`;

            try {
              log.info(`[setContext] Re-initializing entityStore with CRDT room: ${crdtRoomId}`);
              await useEntityStore.getState().initialize(crdtRoomId);
              log.info(`[setContext] ✅ EntityStore re-initialized for dataspace: ${dataspaceKey}`);
            } catch (fetchError) {
              log.error(`[setContext] Failed to re-initialize entityStore for ${dataspaceKey}:`, fetchError);
            }
            
          } catch (error) {
            log.error('[setContext] Failed to switch entityStore dataspace:', error);
          }
          
          log.info(`[setContext] Switched to ${context.type} dataspace`);
          
        } catch (error: any) {
          log.error('[setContext] Error:', error);
          set({ error: error.message || 'Failed to switch dataspace' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Load user's organizations
      loadUserOrgs: async () => {
        set({ isLoading: true, error: null });
        
        try {
          log.info('[loadUserOrgs] Loading user orgs');
          
          const orgs: Org[] = await fetchService(`${API_BASE_URL}/orgs`, {
            method: 'GET'
          });
          
          // Check for new org memberships and show notifications
          const { checkForNewOrgs, showNewOrgNotification, markOrgsAsSeen } = await import('../utils/orgNotifications');
          const currentOrgIds = orgs.map(org => org._id || org.id);
          const newOrgIds = checkForNewOrgs(currentOrgIds);
          
          // Show notifications for new orgs (excluding personal orgs)
          if (newOrgIds.length > 0) {
            log.info(`[loadUserOrgs] Found ${newOrgIds.length} new org(s), showing notifications`);
            newOrgIds.forEach((newOrgId: string) => {
              const org = orgs.find(o => (o._id || o.id) === newOrgId);
              if (org && !isPersonalOrg(org)) {
                const orgName = org.skeleton.displayName || org.name;
                showNewOrgNotification(newOrgId, orgName, (orgId: string) => {
                  // Switch to the org when button is clicked
                  get().switchToOrg(orgId);
                });
              }
            });
            
            // Mark all orgs as seen
            markOrgsAsSeen(currentOrgIds);
          }
          
          set({ userOrgs: orgs });
          
          log.info(`[loadUserOrgs] Loaded ${orgs.length} orgs`);
          
        } catch (error: any) {
          log.error('[loadUserOrgs] Error:', error);
          set({ error: error.message || 'Failed to load organizations' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Switch to personal dataspace (the user's personal org)
      switchToPersonal: async () => {
        const currentPath = window.location.pathname;
        const currentSpaceId = currentPath.match(/\/spaces\/([^\/]+)/)?.[1];

        // Resolve the user's personal org id from the backend before switching.
        // GET /user/context always returns the real personal org (never a bare 'personal' type).
        let personalOrgId: string | null = null;
        let personalOrgName: string | null = null;
        try {
          const ctx = await fetchService(`${API_BASE_URL}/user/context`, { method: 'GET' });
          // The server resolves 'personal' alias to the actual personal org.
          personalOrgId = ctx.org_id || null;
          personalOrgName = ctx.org_name || null;
        } catch (err) {
          log.warn('[switchToPersonal] Could not resolve personal org from backend:', err);
        }

        // Check if already on the personal org (no-op)
        const currentContext = get().currentContext;
        if (personalOrgId && currentContext.orgId === personalOrgId) {
          log.info(`[switchToPersonal] Already on personal org ${personalOrgId.slice(-8)}, no-op`);
          return;
        }

        await get().setContext({
          type: 'org',
          orgId: personalOrgId,
          orgName: personalOrgName,
        });
        
        // If currently on a space, it MUST be an org space (since we just switched FROM org)
        // Therefore we need to navigate to a personal space
        if (currentSpaceId) {
          log.info(`[switchToPersonal] Currently on space ${currentSpaceId} (must be org space), finding personal space`);
        }
        
        // Find a personal space to navigate to
        try {
          // Fetch all spaces to find personal ones
          const allSpaces = await fetchService(`${API_BASE_URL}/entities?entityType=Space`, {
            method: 'GET'
          });
          
          const personalSpaces = allSpaces.filter((s: any) => !s.org_id);
          log.info(`[switchToPersonal] Found ${personalSpaces.length} personal spaces`);
          
          if (personalSpaces.length === 0) {
            // No personal spaces - create one
            log.info(`[switchToPersonal] No personal spaces found, creating one`);
            const newSpace = await fetchService(`${API_BASE_URL}/spaces`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: 'New Space',
                metadata: { is_default: true }
              })
            });
            
            if (newSpace && newSpace._id) {
              log.info(`[switchToPersonal] Created personal space: ${newSpace._id}`);
              get().setPendingNavigation({ type: 'space', spaceId: newSpace._id });
              get().setLastVisitedSpace(newSpace._id);
            }
            return;
          }
          
          // Try last visited personal space first
          const lastVisitedSpace = get().getLastVisitedSpace('personal');
          if (lastVisitedSpace && personalSpaces.some((s: any) => s._id === lastVisitedSpace)) {
            log.info(`[switchToPersonal] Navigating to last visited personal space: ${lastVisitedSpace}`);
            get().setPendingNavigation({ type: 'space', spaceId: lastVisitedSpace });
            return;
          }
          
          // Navigate to most recent personal space
          const mostRecent = personalSpaces.sort((a: any, b: any) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          )[0];
          
          if (mostRecent) {
            log.info(`[switchToPersonal] Navigating to most recent personal space: ${mostRecent._id}`);
            get().setPendingNavigation({ type: 'space', spaceId: mostRecent._id });
            get().setLastVisitedSpace(mostRecent._id);
          }
        } catch (error) {
          log.error(`[switchToPersonal] Error finding personal spaces:`, error);
          // Stay on current page if we can't find personal spaces
        }
      },
      
      // Switch to org dataspace
      switchToOrg: async (orgId: string) => {
        const org = get().userOrgs.find(o => o._id === orgId || o.id === orgId);
        
        if (!org) {
          throw new Error(`Organization ${orgId} not found`);
        }
        
        // ✅ Check if payment is required
        const billingStatus = org.skeleton.billing_status;
        if (billingStatus === 'payment_required' || billingStatus === 'past_due') {
          log.info(`[switchToOrg] ⚠️ Payment required for org ${orgId}, showing payment gate`);
          get().showPaymentGate(orgId);
          return; // Block switch
        }
        
        // Check if we're already in this org context (no-op)
        const currentContext = get().currentContext;
        if (currentContext.type === 'org' && currentContext.orgId === orgId) {
          log.info(`[switchToOrg] Already in org ${orgId} dataspace, no-op`);
          return;
        }
        
        // Check if currently on a space page
        const currentPath = window.location.pathname;
        const currentSpaceId = currentPath.match(/\/spaces\/([^\/]+)/)?.[1];
        
        await get().setContext({
          type: 'org',
          orgId: org._id || org.id,
          orgName: org.skeleton.displayName || org.name
        });
        
        // Check if user has any Spaces in this org dataspace
        try {
          const fetchService = (await import('../services/fetchService')).default;
          const { API_BASE_URL } = await import('../api/config');
          
          // Query for Spaces in this org
          log.info(`[switchToOrg] 🔍 Checking for Spaces in org: ${orgId}`);
          
          let spaces = [];
          let apiFailed = false;
          try {
            const allSpaces = await fetchService(`${API_BASE_URL}/entities?entityType=Space`, {
              method: 'GET'
            });
            
            log.info(`[switchToOrg] 📊 Fetched ${allSpaces?.length || 0} total Spaces`);
            
            // Filter client-side for spaces with matching org_id
            if (allSpaces && Array.isArray(allSpaces)) {
              spaces = allSpaces.filter((s: any) => {
                const matches = s.org_id === orgId;
                if (matches) {
                  log.info(`[switchToOrg] ✓ Space "${s.name}" (${s._id}) has org_id: ${s.org_id}`);
                }
                return matches;
              });
            }
            
            log.info(`[switchToOrg] ✅ Found ${spaces.length} Spaces with org_id=${orgId}`);
            
            // Since we passed the early return check above, we're definitely switching FROM
            // a different dataspace. Therefore, current space (if any) does NOT belong to this org.
            // We need to find an org space to navigate to.
            
            // Also log a few Spaces for debugging
            if (allSpaces && allSpaces.length > 0) {
              const sample = allSpaces.slice(0, 3);
              log.info(`[switchToOrg] 📋 Sample Spaces:`, sample.map((s: any) => ({
                name: s.name,
                org_id: s.org_id,
                metadata_org_id: s.metadata?.org_id
              })));
            }
          } catch (fetchError) {
            log.error('[switchToOrg] ❌ Backend error fetching Spaces - this is a bug!', fetchError);
            apiFailed = true;
            spaces = [];
          }
          
          if (spaces.length === 0) {
            if (apiFailed) {
              // API failed - should not create a new space, just throw the error
              log.error('[switchToOrg] ⚠️ Cannot switch to org: backend API failed');
              throw new Error('Failed to fetch Spaces from backend. Please refresh the page and try again.');
            }
            
            // No Spaces exist - create one (only happens once per org)
            log.info('[switchToOrg] No Spaces found for org, creating default Space');
            
            const newSpace = await fetchService(`${API_BASE_URL}/spaces`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: `${org.skeleton.displayName} Workspace`,
                org_id: orgId,  // Top-level for proper scoping
                metadata: {
                  is_default: true,
                  created_by_dataspace_switch: true
                }
              })
            });
            
            if (newSpace && newSpace._id) {
              log.info(`[switchToOrg] Created new Space: ${newSpace._id}`);
              get().setPendingNavigation({ type: 'space', spaceId: newSpace._id });
              // Remember this as last visited
              get().setLastVisitedSpace(newSpace._id);
            }
          } else {
            // Spaces exist - navigate to last visited or most recent
            const lastVisitedSpace = get().getLastVisitedSpace(orgId);
            
            if (lastVisitedSpace && spaces.some((s: any) => s._id === lastVisitedSpace)) {
              // Last visited Space still exists
              log.info(`[switchToOrg] Navigating to last visited Space: ${lastVisitedSpace}`);
              get().setPendingNavigation({ type: 'space', spaceId: lastVisitedSpace });
            } else {
              // Navigate to most recent Space
              const mostRecent = spaces.sort((a: any, b: any) => 
                new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
              )[0];
              
              if (mostRecent) {
                log.info(`[switchToOrg] Navigating to most recent Space: ${mostRecent._id}`);
                get().setPendingNavigation({ type: 'space', spaceId: mostRecent._id });
                get().setLastVisitedSpace(mostRecent._id);
              }
            }
          }
        } catch (error) {
          log.error('[switchToOrg] Error checking/creating Spaces:', error);
          // Stay on current page if error occurs
        }
      },
      
      // Refresh context from backend
      refreshContext: async () => {
        set({ isLoading: true, error: null });
        
        try {
          log.info('[refreshContext] Refreshing context from backend');
          
          const data = await fetchService(`${API_BASE_URL}/user/context`, {
            method: 'GET'
          });
          
          set({
            currentContext: {
              type: data.type,
              orgId: data.org_id || null,
              orgName: data.org_name || null
            }
          });
          
          log.info('[refreshContext] Context refreshed:', data);
          
        } catch (error: any) {
          log.error('[refreshContext] Error:', error);
          set({ error: error.message || 'Failed to refresh context' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Clear error
      clearError: () => {
        set({ error: null });
      },
      
      // ✅ FIX: Set pending navigation (replaces sessionStorage)
      setPendingNavigation: (nav: PendingNavigation | null) => {
        set({ pendingNavigation: nav });
      },
      
      // Set last visited space for current dataspace
      setLastVisitedSpace: (spaceId: string) => {
        const { currentContext, lastVisitedSpaces } = get();
        const contextKey = currentContext.type === 'personal' ? 'personal' : currentContext.orgId || '';
        
        if (contextKey) {
          set({ 
            lastVisitedSpaces: {
              ...lastVisitedSpaces,
              [contextKey]: spaceId
            }
          });
          log.info(`[setLastVisitedSpace] Set last visited space for ${contextKey}: ${spaceId}`);
        }
      },
      
      // Get last visited space for a dataspace context
      getLastVisitedSpace: (contextKey?: string) => {
        const { currentContext, lastVisitedSpaces } = get();
        const key = contextKey || (currentContext.type === 'personal' ? 'personal' : currentContext.orgId || '');
        
        return key ? lastVisitedSpaces[key] || null : null;
      },
      
      // Show payment gate for unpaid org
      showPaymentGate: (orgId: string) => {
        const org = get().userOrgs.find(o => o._id === orgId || o.id === orgId);
        
        if (org) {
          set({
            paymentGate: {
              isOpen: true,
              orgId: org._id || org.id,
              orgName: org.skeleton.displayName || org.name,
              seatCount: org.skeleton.seat_count || 1
            }
          });
        }
      },
      
      // Hide payment gate
      hidePaymentGate: () => {
        set({
          paymentGate: {
            isOpen: false,
            orgId: null,
            orgName: null,
            seatCount: 1,
            planTier: undefined
          }
        });
      },
      
      // ✅ FIX #6: Update payment gate plan tier
      updatePaymentGatePlan: (planTier: string) => {
        const current = get().paymentGate;
        set({
          paymentGate: {
            ...current,
            planTier
          }
        });
      }
    }),
    {
      name: 'dataspace-context',
      // Only persist lastVisitedSpaces — NOT currentContext.
      // currentContext is re-synced from the backend on every App load via GET /user/context.
      // Persisting it caused race conditions where a stale { type: 'personal', orgId: null }
      // was read before the backend sync completed, producing empty dataspaceIds on agent runs.
      partialize: (state) => ({
        lastVisitedSpaces: state.lastVisitedSpaces,
      }),
    }
  )
);

// Helper functions for use outside the store

/**
 * Get the current dataspace context
 */
export const getCurrentDataspaceContext = (): DataspaceContext => {
  return useDataspaceContextStore.getState().currentContext;
};

/**
 * Check if currently in an org dataspace
 */
export const isInOrgDataspace = (): boolean => {
  return getCurrentDataspaceContext().type === 'org';
};

/**
 * Get current org ID (if in org dataspace)
 */
export const getCurrentOrgId = (): string | null => {
  const context = getCurrentDataspaceContext();
  return context.type === 'org' ? context.orgId : null;
};

/**
 * Initialize dataspace context on app start
 */
export const initializeDataspaceContext = async () => {
  const store = useDataspaceContextStore.getState();
  
  try {
    // Load user's orgs
    await store.loadUserOrgs();
    
    // Refresh context from backend (in case it changed on another device)
    await store.refreshContext();
    
    log.info('[initializeDataspaceContext] Dataspace context initialized');
  } catch (error) {
    log.error('[initializeDataspaceContext] Failed to initialize:', error);
  }
};


